/**
 * /downloads — official XER software installers (Mission Planner, UC Flasher,
 * H16 QGC).
 *
 * Read-only for every signed-in user. One column per product THE CALLER CAN
 * SEE — columns are derived from the API response, and RLS audience-gates the
 * rows server-side (internal products like UC Flasher are simply absent for
 * customers). Each column splits into STABLE (labeled spec table:
 * version/size/released/sha256/notes), PRE-RELEASE (non-stable releases newer
 * than the stable one — only when they exist), and LEGACY (every release older
 * than the stable one). The download button is pinned to the card bottom so
 * all columns align regardless of notes length.
 * Publishing/deleting lives in Admin Tools → Software Releases. Downloads are
 * presigned S3 links; each issued link is recorded server-side for the
 * SAIL III audit trail.
 */
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { extractErrorMessage } from '../utils/errorHandler';
import { pendingTargetId } from '../utils/pendingTargetId';
import {
  useDownloadReleaseMutation,
  useSoftwareReleasesQuery,
} from '../components/Downloads/Downloads.queries';
import { formatBytes } from '../components/Downloads/formatBytes';
import { releaseSlotHeading } from '../components/Downloads/releaseSlotHeading';
import {
  orderProductsFor,
  productSortOrders,
  releaseProductLabel,
} from '../components/Downloads/productCatalog';
import { SoftwareRelease } from '../types/downloads';

const LABEL_CELL_SX = {
  color: 'text.secondary',
  width: 96,
  verticalAlign: 'top',
  whiteSpace: 'nowrap',
} as const;

const SECTION_SX = { mt: 2 } as const;

export const DownloadsPage: React.FC = () => {
  const navigate = useNavigate();
  const releasesQuery = useSoftwareReleasesQuery();
  const downloadMutation = useDownloadReleaseMutation();

  const releases = releasesQuery.data ?? [];

  // Which RELEASE is being downloaded (#1312). One mutation instance serves
  // every button on the page — each product column's headline button plus every
  // row of its Pre-release and Legacy tables — so `downloadMutation.isPending`
  // is a property of the PAGE, not of a release. Wired straight to `disabled`
  // it greyed out all of them the moment any one was clicked: a user fetching
  // Mission Planner could not start the H16 QGC download until the first
  // presign returned, and on the H16's own browser (the slowest client we
  // support, and the one /downloads exists for) that reads as a dead page.
  //
  // The mutation's `variables` IS the release id, and the headline button
  // targets `stable.id` — a release that never also appears in the tables below
  // — so one id gates both surfaces without either disabling the other's rows.
  // `pendingTargetId` checks `isPending` first; see its docstring for why that
  // ordering is the whole point.
  const downloadingReleaseId = pendingTargetId(
    downloadMutation.isPending,
    downloadMutation.variables
  );

  // Column order comes from the server's software_products.sort_order when the
  // API embedded it, so a product created after this bundle was built lands
  // where the admin put it rather than alphabetically at the end.
  const sortOrders = productSortOrders(releases);
  // List is served newest-first. The headline slot carries the newest stable
  // release; a non-stable one only fills in when no stable exists for that
  // product (and then shows its channel so it can't masquerade as stable —
  // both the chip AND the heading, which reads "Latest" rather than "Stable"
  // in that case; see releaseSlotHeading).
  const stableByProduct = new Map<string, SoftwareRelease>();
  for (const release of releases) {
    const current = stableByProduct.get(release.product);
    if (!current || (current.channel !== 'stable' && release.channel === 'stable')) {
      stableByProduct.set(release.product, release);
    }
  }

  const handleDownload = async (release: SoftwareRelease) => {
    try {
      const link = await downloadMutation.mutateAsync(release.id);
      // Anchor-click (AttachmentList precedent) so the browser saves with the
      // real installer filename from the signed Content-Disposition.
      const anchor = document.createElement('a');
      anchor.href = link.download_url;
      anchor.download = link.file_name;
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to start download'));
    }
  };

  const copySha = (sha256: string) => {
    void navigator.clipboard.writeText(sha256);
    toast.success('SHA256 copied');
  };

  const channelChip = (release: SoftwareRelease) =>
    release.channel !== 'stable' ? (
      <Chip size="small" color="warning" variant="outlined" label={release.channel} />
    ) : null;

  const shaValue = (release: SoftwareRelease) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ fontFamily: 'monospace' }}>
        {release.sha256.slice(0, 12)}…
      </Box>
      <Tooltip title={`${release.sha256} (click to copy)`}>
        <IconButton aria-label="Copy SHA256" size="small" onClick={() => copySha(release.sha256)}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const sectionTitle = (title: string) => (
    <Box sx={SECTION_SX}>
      <Typography variant="overline" color="text.secondary" component="div">
        {title}
      </Typography>
      <Divider />
    </Box>
  );

  // Compact one-line-per-release table used by PRE-RELEASE and LEGACY.
  // tableLayout:'fixed' so the version column truly ellipsizes — under the
  // default auto layout a nowrap cell can force the table past the card edge.
  const releaseListTable = (rows: SoftwareRelease[], testId: string) => (
    <Table size="small" sx={{ tableLayout: 'fixed' }} data-testid={testId}>
      <TableBody>
        {rows.map((release) => (
          <TableRow key={release.id}>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="span"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  v{release.version}
                </Box>
                {channelChip(release)}
              </Box>
            </TableCell>
            <TableCell
              align="right"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap', width: 136 }}
            >
              {formatBytes(release.size_bytes)} ·{' '}
              {new Date(release.released_at).toLocaleDateString()}
            </TableCell>
            <TableCell align="right" sx={{ width: 48 }}>
              <Tooltip title={`Download · SHA256 ${release.sha256.slice(0, 12)}…`}>
                <IconButton
                  aria-label={`Download v${release.version}`}
                  size="small"
                  onClick={() => void handleDownload(release)}
                  disabled={downloadingReleaseId === release.id}
                  data-testid="download-release-btn"
                  data-release-id={release.id}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="downloads-page">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1">
            Downloads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Official XER software. Verify your download against the published SHA256.
          </Typography>
        </Box>
      </Box>

      {releasesQuery.isLoading && (
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} />
      )}
      {releasesQuery.isError && (
        <Alert severity="error">Failed to load software releases. Try reloading the page.</Alert>
      )}

      {releasesQuery.isSuccess && releases.length === 0 && (
        <Alert severity="info" data-testid="no-releases">
          No software releases are available yet.
        </Alert>
      )}

      {releasesQuery.isSuccess && releases.length > 0 && (
        <Grid container spacing={3}>
          {/* Columns are derived from the rows the API returned — visibility is
              decided server-side by RLS (audience gating), so a product the
              caller may not see simply has no column. */}
          {orderProductsFor([...new Set(releases.map((r) => r.product))], sortOrders).map(
            (product) => {
              const stable = stableByProduct.get(product);
              if (!stable) return null; // unreachable: product came from releases
              const others = releases.filter((r) => r.product === product && r.id !== stable.id);
              // A non-stable release published after the current stable must not
              // masquerade as "legacy" — it gets its own PRE-RELEASE group.
              const stableTime = new Date(stable.released_at).getTime();
              const preRelease = others.filter(
                (r) => new Date(r.released_at).getTime() > stableTime
              );
              const legacy = others.filter((r) => new Date(r.released_at).getTime() <= stableTime);

              return (
                <Grid item xs={12} md={6} lg={4} key={product} sx={{ display: 'flex' }}>
                  <Card
                    variant="outlined"
                    sx={{ width: '100%' }}
                    data-testid={`latest-card-${product}`}
                  >
                    <CardContent
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 2 }}
                    >
                      <Typography variant="h6">{releaseProductLabel(releases, product)}</Typography>

                      <Box sx={{ ...SECTION_SX, mt: 1 }}>
                        <Typography variant="overline" color="text.secondary" component="div">
                          {releaseSlotHeading(stable.channel)}
                        </Typography>
                        <Divider />
                      </Box>
                      <Table
                        size="small"
                        sx={{ '& td': { px: 0 } }}
                        data-testid={`stable-release-${product}`}
                      >
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={LABEL_CELL_SX}>
                              Version
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip size="small" color="primary" label={`v${stable.version}`} />
                                {channelChip(stable)}
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={LABEL_CELL_SX}>
                              Size
                            </TableCell>
                            <TableCell>{formatBytes(stable.size_bytes)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={LABEL_CELL_SX}>
                              Released
                            </TableCell>
                            <TableCell>
                              {new Date(stable.released_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={LABEL_CELL_SX}>
                              SHA256
                            </TableCell>
                            <TableCell>{shaValue(stable)}</TableCell>
                          </TableRow>
                          {stable.release_notes && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={LABEL_CELL_SX}>
                                Notes
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                                {stable.release_notes}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>

                      {preRelease.length > 0 && (
                        <>
                          {sectionTitle('Pre-release')}
                          {releaseListTable(preRelease, `prerelease-list-${product}`)}
                        </>
                      )}

                      {legacy.length > 0 && (
                        <>
                          {sectionTitle('Legacy')}
                          {releaseListTable(legacy, `legacy-list-${product}`)}
                        </>
                      )}

                      {product === 'h16_qgc' && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mt: 2,
                            '&:before': { display: 'none' },
                            border: 1,
                            borderColor: 'divider',
                          }}
                          data-testid="h16-install-help"
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2">Installing on the H16</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2" component="div" color="text.secondary">
                              <ol style={{ margin: 0, paddingLeft: '1.2em' }}>
                                <li>
                                  First remove old QGC versions — uninstall the app itself and also
                                  delete any leftover QGC APKs from the Download folder.
                                </li>
                                <li>
                                  Make sure at least 1&nbsp;GB of internal storage is free (Settings
                                  → Storage) — clear app caches or old downloads if you&apos;re
                                  below that.
                                </li>
                                <li>
                                  Download the release with the H16&apos;s browser and let it finish
                                  (~160&nbsp;MB).
                                </li>
                                <li>
                                  One-time: allow installs — Settings → Security → enable
                                  &quot;Unknown sources&quot;.
                                </li>
                                <li>
                                  Open the finished download from the notification shade or the file
                                  manager (Download folder) and tap Install.
                                </li>
                                <li>
                                  If Android answers &quot;App not installed&quot; or complains
                                  about storage, redo steps 1–2, then install again (QGC settings on
                                  the device must be set up again after a reinstall).
                                </li>
                                <li>Open QGC and check the version under Application Settings.</li>
                              </ol>
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => void handleDownload(stable)}
                          disabled={downloadingReleaseId === stable.id}
                          data-testid={`download-latest-${product}`}
                          data-release-id={stable.id}
                        >
                          Download v{stable.version}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            }
          )}
        </Grid>
      )}
    </Container>
  );
};

export default DownloadsPage;
